<template>
    <div class="container">
        <vrm-form @submit="submit">
            <template v-if="step === 1">
                <div class="row">
                    <div class="col-sm-12 col-md-10 col-md-offset-1">
                        <h1 class="vrm-page-title">
                            {{
                                $t('mfa.sms_wizard_title', {
                                    i18nFallback: 'Setting up SMS verification',
                                })
                            }}
                        </h1>
                        <p>
                            {{
                                $t('mfa.sms_wizard_explainer_step1', {
                                    i18nFallback:
                                        'Please enter the phonenumber you wish to receive SMS verification tokens on.',
                                })
                            }}
                        </p>
                    </div>
                </div>
                <div class="row">
                    <div class="col-xs-12 col-sm-6 col-md-4 col-md-offset-1">
                        <vrm-dropdown-field
                            v-model="prefix"
                            :title="
                                $t('mfa.sms_wizard_country_prefix', {
                                    i18nFallback: 'Country prefix',
                                })
                            "
                            :options="prefixes"
                            :disabled="busy"
                            search-enabled
                            required
                        />
                    </div>
                    <div class="col-xs-12 col-sm-6 col-md-4 col-md-offset-1">
                        <vrm-text-field
                            v-model="phone"
                            :title="
                                $t('mfa.sms_wizard_phonenumber', { i18nFallback: 'Phonenumber' })
                            "
                            :disabled="busy"
                            autocomplete="telephone"
                            required
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-xs-12 col-md-9 col-md-offset-1">
                        <div class="vrm-form__fieldset-item">
                            <button
                                type="submit"
                                class="sg-btn sg-blue pull-right"
                                :disabled="busy"
                            >
                                {{ $t('mfa.sms_wizard_next', { i18nFallback: 'Next' }) }}
                            </button>
                            <div id="g-recaptcha" data-size="invisible"></div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else-if="step === 2">
                <div class="row">
                    <div class="col-sm-12 col-md-10 col-md-offset-1">
                        <h1 class="vrm-page-title">
                            {{
                                $t('mfa.sms_wizard_title', {
                                    i18nFallback: 'Setting up SMS verification',
                                })
                            }}
                        </h1>
                        <p>
                            {{
                                $t('mfa.sms_wizard_explainer_step2', {
                                    i18nFallback:
                                        'We have sent a test code to the given phonenumber. Enter the received code to activate two-step SMS verification on your account.',
                                })
                            }}
                        </p>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-12 col-md-10 col-md-offset-1">
                        <p>
                            <b>
                                {{
                                    $t('mfa.sms_wizard_phonenumber', {
                                        i18nFallback: 'Phonenumber',
                                    })
                                }}:
                            </b>
                            <span>(+{{ prefix.code }}) {{ phone }}</span>
                            (
                            <a @click.prevent="step = 1">
                                {{ $t('mfa.sms_wizard_change', { i18nFallback: 'Change' }) }}
                            </a>
                            )
                        </p>
                    </div>
                </div>
                <div class="row">
                    <div class="col-xs-12 col-md-5 col-md-offset-1">
                        <vrm-text-field
                            v-model="code"
                            :title="$t('mfa.sms_wizard_token', { i18nFallback: 'Code' })"
                            :disabled="busy"
                            autocomplete="telephone"
                            required
                        />
                    </div>
                    <div class="col-xs-12 col-md-1">
                        <div class="vrm-form__fieldset-item">
                            <label class="sg-textfield-label">&nbsp;</label>
                            <button type="submit" class="sg-btn sg-blue" :disabled="busy">
                                {{ $t('mfa.sms_wizard_verify', { i18nFallback: 'Verify' }) }}
                            </button>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else-if="step === 3">
                <div class="row">
                    <div class="col-sm-12 col-md-10 col-md-offset-1">
                        <h1 class="vrm-page-title">
                            {{
                                $t('mfa.sms_wizard_success_title', {
                                    i18nFallback: 'Two-step verification enabled',
                                })
                            }}
                        </h1>
                        <p>
                            {{
                                $t('mfa.sms_wizard_success_explainer', {
                                    i18nFallback:
                                        'You have succesfully configured two-step veriication with SMS. From now on you will need to use SMS verification during login. You can change these settings in your profile at any time.',
                                })
                            }}
                        </p>
                        <p>
                            <router-link to="base.profile.twofactor">
                                {{
                                    $t('mfa.back_to_settings', { i18nFallback: 'Back to settings' })
                                }}
                            </router-link>
                        </p>
                    </div>
                </div>
            </template>
        </vrm-form>
    </div>
</template>
<style scoped lang="scss"></style>
<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import WizardMixin from '@/vue/components/pages/mfa/wizards/WizardMixin';
import VrmValidation from '@/vue/components/VrmValidation';
import VrmTextField from '@/vue/components/forms/VrmTextField';
import VrmDropdownField from '@/vue/components/forms/VrmDropdownField';
import VrmForm from '@/vue/components/forms/VrmForm';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { vrmConfig } from '@/config';

@Component({
    components: { VrmForm, VrmDropdownField, VrmTextField, VrmValidation, VrmDropdown },
    mixins: [WizardMixin],
})
@AngularRoute('base.profile.twofactor.sms', '/sms')
@AngularLocaleBundle('mfa')
export default class Sms extends Vue {
    @ngInject()
    ApiService;
    @ngInject()
    vcRecaptchaService;

    grecaptcha = window.grecaptcha;

    step = 1;
    busy = false;

    prefix = null;
    phone = '';
    code = '';
    recaptcha = '';

    mounted() {
        this.grecaptcha.render('g-recaptcha', {
            sitekey: vrmConfig.recaptchaKey,
            callback: this.onRecaptchaSuccess,
            'expire-callback': this.onRecaptchaExpired,
        });
    }

    get prefixes() {
        return COUNTRY_PREFIXES;
    }

    get fullPhonenumber() {
        if (this.prefix) {
            return `+${this.prefix.code}${this.phone}`;
        } else {
            return this.phone;
        }
    }

    submit() {
        switch (this.step) {
            case 1:
                return this.submitStep1();
            case 2:
                return this.submitStep2();
        }
    }

    onRecaptchaExpired() {
        this.recaptcha = '';
    }

    onRecaptchaSuccess(response) {
        this.recaptcha = response;
        this.submitStep1();
    }

    async submitStep1() {
        this.busy = true;

        if (!this.recaptcha) {
            this.grecaptcha.execute();
            this.busy = false;
            return;
        }

        try {
            // Submit details to backend
            if (await this.sendTestCode()) {
                this.step = 2;
            } else {
                this.recaptcha = '';
                this.grecaptcha.reset();
            }
        } finally {
            this.busy = false;
        }
    }

    async sendTestCode() {
        return await this.$store.dispatch('MfaService/smsSetup', [
            this.fullPhonenumber,
            this.recaptcha,
        ]);
    }

    async submitStep2() {
        this.busy = true;
        try {
            if (await this.verifyTestCode()) {
                this.step = 3;
            }
        } finally {
            this.busy = false;
        }
    }

    async verifyTestCode() {
        return await this.$store.dispatch('MfaService/smsVerify', [
            this.fullPhonenumber,
            this.code,
        ]);
    }
}

const COUNTRY_PREFIXES = [
    { code: 1, name: 'United States of America (+1)' },
    { code: 1, name: 'Canada (+1)' },
    { code: 7, name: 'Russia (+7)' },
    { code: 7, name: 'Kazakhstan (+7)' },
    { code: 20, name: 'Egypt (+20)' },
    { code: 27, name: 'South Africa (+27)' },
    { code: 30, name: 'Greece (+30)' },
    { code: 31, name: 'Netherlands (+31)' },
    { code: 32, name: 'Belgium (+32)' },
    { code: 33, name: 'France (+33)' },
    { code: 34, name: 'Spain (+34)' },
    { code: 36, name: 'Hungary (+36)' },
    { code: 39, name: 'Italy (+39)' },
    { code: 40, name: 'Romania (+40)' },
    { code: 41, name: 'Switzerland (+41)' },
    { code: 43, name: 'Austria (+43)' },
    { code: 44, name: 'United Kingdom (+44)' },
    { code: 44, name: 'Guernsey (+44)' },
    { code: 44, name: 'Isle of Man (+44)' },
    { code: 44, name: 'Jersey (+44)' },
    { code: 45, name: 'Denmark (+45)' },
    { code: 46, name: 'Sweden (+46)' },
    { code: 47, name: 'Norway (+47)' },
    { code: 48, name: 'Poland (+48)' },
    { code: 49, name: 'Germany (+49)' },
    { code: 51, name: 'Peru (+51)' },
    { code: 52, name: 'Mexico (+52)' },
    { code: 53, name: 'Cuba (+53)' },
    { code: 54, name: 'Argentina (+54)' },
    { code: 55, name: 'Brazil (+55)' },
    { code: 56, name: 'Chile (+56)' },
    { code: 57, name: 'Colombia (+57)' },
    { code: 58, name: 'Venezuela (+58)' },
    { code: 60, name: 'Malaysia (+60)' },
    { code: 61, name: 'Australia (+61)' },
    { code: 62, name: 'Indonesia (+62)' },
    { code: 63, name: 'Philippines (+63)' },
    { code: 64, name: 'New Zealand (+64)' },
    { code: 65, name: 'Singapore (+65)' },
    { code: 66, name: 'Thailand (+66)' },
    { code: 81, name: 'Japan (+81)' },
    { code: 82, name: 'Korea (+South) (+82)' },
    { code: 84, name: 'Vietnam (+84)' },
    { code: 86, name: 'China (+86)' },
    { code: 90, name: 'Turkey (+90)' },
    { code: 91, name: 'India (+91)' },
    { code: 92, name: 'Pakistan (+92)' },
    { code: 93, name: 'Afghanistan (+93)' },
    { code: 94, name: 'Sri Lanka (+94)' },
    { code: 95, name: 'Myanmar (+95)' },
    { code: 98, name: 'Iran (+98)' },
    { code: 212, name: 'Morocco (+212)' },
    { code: 213, name: 'Algeria (+213)' },
    { code: 216, name: 'Tunisia (+216)' },
    { code: 218, name: 'Libya (+218)' },
    { code: 220, name: 'Gambia (+220)' },
    { code: 221, name: 'Senegal (+221)' },
    { code: 222, name: 'Mauritania (+222)' },
    { code: 223, name: 'Mali Republic (+223)' },
    { code: 224, name: 'Guinea (+224)' },
    { code: 225, name: 'Ivory Coast (+225)' },
    { code: 226, name: 'Burkina Faso (+226)' },
    { code: 227, name: 'Niger (+227)' },
    { code: 228, name: 'Togo (+228)' },
    { code: 229, name: 'Benin (+229)' },
    { code: 230, name: 'Mauritius (+230)' },
    { code: 231, name: 'Liberia (+231)' },
    { code: 232, name: 'Sierra Leone (+232)' },
    { code: 233, name: 'Ghana (+233)' },
    { code: 234, name: 'Nigeria (+234)' },
    { code: 235, name: 'Chad (+235)' },
    { code: 236, name: 'Central African Republic (+236)' },
    { code: 237, name: 'Cameroon (+237)' },
    { code: 238, name: 'Cape Verde Islands (+238)' },
    { code: 239, name: 'Sao Tome and Principe (+239)' },
    { code: 241, name: 'Gabon (+241)' },
    { code: 243, name: 'Congo, Democratic Republ (+243)' },
    { code: 244, name: 'Angola (+244)' },
    { code: 245, name: 'Guinea-Bissau (+245)' },
    { code: 248, name: 'Seychelles (+248)' },
    { code: 249, name: 'Sudan (+249)' },
    { code: 250, name: 'Rwanda (+250)' },
    { code: 251, name: 'Ethiopia (+251)' },
    { code: 252, name: 'Somalia (+252)' },
    { code: 253, name: 'Djibouti (+253)' },
    { code: 254, name: 'Kenya (+254)' },
    { code: 255, name: 'Tanzania (+255)' },
    { code: 256, name: 'Uganda (+256)' },
    { code: 257, name: 'Burundi (+257)' },
    { code: 258, name: 'Mozambique (+258)' },
    { code: 260, name: 'Zambia (+260)' },
    { code: 261, name: 'Madagascar (+261)' },
    { code: 262, name: 'Reunion (+262)' },
    { code: 263, name: 'Zimbabwe (+263)' },
    { code: 264, name: 'Namibia (+264)' },
    { code: 265, name: 'Malawi (+265)' },
    { code: 266, name: 'Lesotho (+266)' },
    { code: 267, name: 'Botswana (+267)' },
    { code: 268, name: 'Swaziland (+268)' },
    { code: 269, name: 'Mayotte Island (+269)' },
    { code: 297, name: 'Aruba (+297)' },
    { code: 298, name: 'Faroe Islands (+298)' },
    { code: 299, name: 'Greenland (+299)' },
    { code: 350, name: 'Gibraltar (+350)' },
    { code: 351, name: 'Portugal (+351)' },
    { code: 352, name: 'Luxembourg (+352)' },
    { code: 353, name: 'Ireland (+353)' },
    { code: 354, name: 'Iceland (+354)' },
    { code: 355, name: 'Albania (+355)' },
    { code: 356, name: 'Malta (+356)' },
    { code: 357, name: 'Cyprus (+357)' },
    { code: 358, name: 'Finland (+358)' },
    { code: 359, name: 'Bulgaria (+359)' },
    { code: 370, name: 'Lithuania (+370)' },
    { code: 371, name: 'Latvia (+371)' },
    { code: 372, name: 'Estonia (+372)' },
    { code: 373, name: 'Moldova (+373)' },
    { code: 374, name: 'Armenia (+374)' },
    { code: 375, name: 'Belarus (+375)' },
    { code: 376, name: 'Andorra (+376)' },
    { code: 377, name: 'Monaco (+377)' },
    { code: 378, name: 'San Marino (+378)' },
    { code: 380, name: 'Ukraine (+380)' },
    { code: 381, name: 'Serbia (+381)' },
    { code: 382, name: 'Montenegro (+382)' },
    { code: 385, name: 'Croatia (+385)' },
    { code: 386, name: 'Slovenia (+386)' },
    { code: 387, name: 'Bosnia-Herzegovina (+387)' },
    { code: 389, name: 'Macedonia (+389)' },
    { code: 420, name: 'Czech Republic (+420)' },
    { code: 421, name: 'Slovakia (+421)' },
    { code: 423, name: 'Liechtenstein (+423)' },
    { code: 500, name: 'Falkland Islands (+500)' },
    { code: 501, name: 'Belize (+501)' },
    { code: 502, name: 'Guatemala (+502)' },
    { code: 503, name: 'El Salvador (+503)' },
    { code: 504, name: 'Honduras (+504)' },
    { code: 505, name: 'Nicaragua (+505)' },
    { code: 506, name: 'Costa Rica (+506)' },
    { code: 507, name: 'Panama (+507)' },
    { code: 509, name: 'Haiti (+509)' },
    { code: 590, name: 'Guadeloupe (+590)' },
    { code: 591, name: 'Bolivia (+591)' },
    { code: 592, name: 'Guyana (+592)' },
    { code: 593, name: 'Ecuador (+593)' },
    { code: 594, name: 'French Guiana (+594)' },
    { code: 595, name: 'Paraguay (+595)' },
    { code: 596, name: 'Martinique (+596)' },
    { code: 597, name: 'Suriname (+597)' },
    { code: 598, name: 'Uruguay (+598)' },
    { code: 599, name: 'Netherlands Antilles (+599)' },
    { code: 670, name: 'Timor-Leste (+670)' },
    { code: 1671, name: 'Guam (+1671)' },
    { code: 673, name: 'Brunei (+673)' },
    { code: 674, name: 'Nauru (+674)' },
    { code: 675, name: 'Papua New Guinea (+675)' },
    { code: 676, name: 'Tonga (+676)' },
    { code: 677, name: 'Solomon Islands (+677)' },
    { code: 678, name: 'Vanuatu (+678)' },
    { code: 679, name: 'Fiji Islands (+679)' },
    { code: 682, name: 'Cook Islands (+682)' },
    { code: 685, name: 'Samoa (+685)' },
    { code: 687, name: 'New Caledonia (+687)' },
    { code: 689, name: 'French Polynesia (+689)' },
    { code: 850, name: 'Korea (+North) (+850)' },
    { code: 852, name: 'HongKong (+852)' },
    { code: 853, name: 'Macau (+853)' },
    { code: 855, name: 'Cambodia (+855)' },
    { code: 856, name: 'Laos (+856)' },
    { code: 880, name: 'Bangladesh (+880)' },
    { code: 882, name: 'International (+882)' },
    { code: 886, name: 'Taiwan (+886)' },
    { code: 960, name: 'Maldives (+960)' },
    { code: 961, name: 'Lebanon (+961)' },
    { code: 962, name: 'Jordan (+962)' },
    { code: 963, name: 'Syria (+963)' },
    { code: 964, name: 'Iraq (+964)' },
    { code: 965, name: 'Kuwait (+965)' },
    { code: 966, name: 'Saudi Arabia (+966)' },
    { code: 967, name: 'Yemen (+967)' },
    { code: 968, name: 'Oman (+968)' },
    { code: 970, name: 'Palestine (+970)' },
    { code: 971, name: 'United Arab Emirates (+971)' },
    { code: 972, name: 'Israel (+972)' },
    { code: 973, name: 'Bahrain (+973)' },
    { code: 974, name: 'Qatar (+974)' },
    { code: 975, name: 'Bhutan (+975)' },
    { code: 976, name: 'Mongolia (+976)' },
    { code: 977, name: 'Nepal (+977)' },
    { code: 992, name: 'Tajikistan (+992)' },
    { code: 993, name: 'Turkmenistan (+993)' },
    { code: 994, name: 'Azerbaijan (+994)' },
    { code: 995, name: 'Georgia (+995)' },
    { code: 996, name: 'Kyrgyzstan (+996)' },
    { code: 998, name: 'Uzbekistan (+998)' },
    { code: 1242, name: 'Bahamas (+1242)' },
    { code: 1246, name: 'Barbados (+1246)' },
    { code: 1264, name: 'Anguilla (+1264)' },
    { code: 1268, name: 'Antigua and Barbuda (+1268)' },
    { code: 1284, name: 'Virgin Islands, British (+1284)' },
    { code: 1345, name: 'Cayman Islands (+1345)' },
    { code: 1441, name: 'Bermuda (+1441)' },
    { code: 1473, name: 'Grenada (+1473)' },
    { code: 1649, name: 'Turks and Caicos Islands (+1649)' },
    { code: 1664, name: 'Montserrat (+1664)' },
    { code: 1758, name: 'Saint Lucia (+1758)' },
    { code: 1767, name: 'Dominica (+1767)' },
    { code: 1784, name: 'St. Vincent and The Gren (+1784)' },
    { code: 1787, name: 'Puerto Rico (+1787)' },
    { code: 1809, name: 'Dominican Republic (+1809)' },
    { code: 1829, name: 'Dominican Republic2 (+1829)' },
    { code: 1849, name: 'Dominican Republic3 (+1849)' },
    { code: 1868, name: 'Trinidad and Tobago (+1868)' },
    { code: 1869, name: 'Saint Kitts and Nevis (+1869)' },
    { code: 1876, name: 'Jamaica (+1876)' },
    { code: 242, name: 'Congo (+242)' },
];
</script>
